import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./lib/auth";
import { SettingsProvider } from "./lib/settings";
import { RealtimeProvider } from "./lib/realtime";
import { ThemeProvider } from "./lib/theme";
import { PatientCacheProvider } from "./lib/patientCache";
import {
  HomeRedirect,
  Protected,
  RequireVerifiedEmail,
  DoctorOnboardingGate,
} from "./lib/routeGuards";
import Layout from "./components/Layout";

// Public + semi-public pages
import Login from "./pages/Login";
import CreatePasswordPage from "./pages/CreatePasswordPage";
import Register from "./pages/Register";
import LegalPage from "./pages/LegalPage";
import VerifyEmail from "./pages/VerifyEmail";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import VerifyYourEmail from "./pages/VerifyYourEmail";
import AppointmentConfirm from "./pages/AppointmentConfirm";
import ScanCapture from "./pages/ScanCapture";
import Onboarding from "./pages/Onboarding";
import SelectSpecialty from "./pages/SelectSpecialty";

// Admin
import AdminOverview from "./pages/admin/Overview";
import AdminUsers from "./pages/admin/Users";
import AdminPatients from "./pages/admin/Patients";
import AdminActivity from "./pages/admin/Activity";
import AdminPresence from "./pages/admin/Presence";
import AdminApiMobile from "./pages/admin/ApiMobile";
import AdminCommissions from "./pages/admin/Commissions";
import AdminPharmacyPayments from "./pages/admin/PharmacyPayments";
import AdminReferrals from "./pages/admin/Referrals";
import PharmacyOrDoctorDetail from "./pages/admin/PharmacyOrDoctorDetail";

// Doctor
import DoctorOverview from "./pages/doctor/Overview";
import DoctorDatabase from "./pages/doctor/Database";
import DoctorAgenda from "./pages/doctor/Agenda";
import DoctorPatients from "./pages/doctor/Patients";
import DoctorPatientDetail from "./pages/doctor/PatientDetail";
import DoctorPrescriptions from "./pages/doctor/Prescriptions";
import DoctorOffices from "./pages/doctor/Offices";
import DoctorFinanzas from "./pages/doctor/Finanzas";
import DoctorReports from "./pages/doctor/Reports";
import FinanceWeekDetail from "./pages/doctor/FinanceWeekDetail";
import DoctorConsultation from "./pages/doctor/Consultation";
import LiveConsultation from "./pages/doctor/LiveConsultation";
import PostConsultReceta from "./pages/doctor/PostConsultReceta";
import PostConsultCertificate from "./pages/doctor/PostConsultCertificate";
import CertificateView from "./pages/doctor/CertificateView";
import PostConsultNextAppt from "./pages/doctor/PostConsultNextAppt";
import DoctorNewAppointment from "./pages/doctor/NewAppointment";
import DoctorNewPatient from "./pages/doctor/NewPatient";
import DoctorReferrals from "./pages/doctor/Referrals";
import ReferralPrint from "./pages/ReferralPrint";
import PublicReferral from "./pages/PublicReferral";
import Aris from "./pages/Aris";

// Secretary
import SecretaryOverview from "./pages/secretary/Overview";
import SecretaryAgenda from "./pages/secretary/Agenda";
import SecretaryPatients from "./pages/secretary/Patients";
import SecretaryPayments from "./pages/secretary/Payments";
import NewPatientQR from "./pages/secretaria/NewPatientQR";

// Pharmacy
import PharmacyInbox from "./pages/pharmacy/Inbox";
import PharmacySales from "./pages/pharmacy/Sales";
import PharmacySalesHistory from "./pages/pharmacy/SalesHistory";

// Cross-role
import Chat from "./pages/Chat";
import PrescriptionPrint from "./pages/PrescriptionPrint";
import PaymentResult from "./pages/PaymentResult";
import ProfilePage from "./pages/ProfilePage";
import InfoPage from "./pages/InfoPage";

// ------------------------------------------------------------------------
// Route configuration. Each entry: [path, element, options?].
// options.gate === true wraps the element with <DoctorOnboardingGate>.
// Routes are restricted by the `roles` array used in renderRoutes().
// ------------------------------------------------------------------------

const ADMIN_ROUTES = [
  ["/admin", <AdminOverview />],
  ["/admin/usuarios", <AdminUsers />],
  ["/admin/pacientes", <AdminPatients />],
  ["/admin/referidos", <AdminReferrals />],
  ["/admin/historial", <AdminActivity />],
  ["/admin/presencia", <AdminPresence />],
  ["/admin/api-movil", <AdminApiMobile />],
  ["/admin/comisiones", <AdminCommissions />],
  ["/admin/pagos-farmacia", <AdminPharmacyPayments />],
  ["/admin/pagos-farmacia/farmacia/:id", <PharmacyOrDoctorDetail kind="pharmacy" />],
  ["/admin/pagos-farmacia/doctor/:id", <PharmacyOrDoctorDetail kind="doctor" />],
  ["/admin/chat", <Chat />],
];

const DOCTOR_ROUTES = [
  ["/doctor", <DoctorOverview />, { gate: true }],
  ["/doctor/agenda", <DoctorAgenda />],
  ["/doctor/agenda/nueva", <DoctorNewAppointment rolePath="doctor" />],
  ["/doctor/consulta/:appointmentId", <LiveConsultation />],
  ["/doctor/consulta/:appointmentId/manual", <LiveConsultation />],
  ["/doctor/consulta/:appointmentId/receta", <PostConsultReceta />],
  ["/doctor/consulta/:appointmentId/certificado", <PostConsultCertificate />],
  ["/doctor/consulta/:appointmentId/siguiente-cita", <PostConsultNextAppt />],
  ["/doctor/certificados/:id", <CertificateView />],
  ["/doctor/pacientes", <DoctorPatients />],
  ["/doctor/pacientes/nuevo", <DoctorNewPatient rolePath="doctor" />],
  ["/doctor/recetas", <DoctorPrescriptions />],
  ["/doctor/oficinas", <DoctorOffices />],
  ["/doctor/finanzas", <DoctorFinanzas />],
  ["/doctor/finanzas/semana/:weekKey", <FinanceWeekDetail />],
  ["/doctor/chat", <Chat />],
  ["/doctor/base-de-datos", <DoctorDatabase />],
  ["/doctor/referidos", <DoctorReferrals />],
  ["/doctor/reportes", <DoctorReports />],
];

const SECRETARY_ROUTES = [
  ["/secretaria", <SecretaryOverview />],
  ["/secretaria/agenda", <SecretaryAgenda />],
  ["/secretaria/agenda/nueva", <DoctorNewAppointment rolePath="secretaria" />],
  ["/secretaria/pacientes", <SecretaryPatients />],
  ["/secretaria/pacientes/nuevo", <DoctorNewPatient rolePath="secretaria" />],
  ["/secretaria/pacientes/nuevo-qr", <NewPatientQR />],
  ["/secretaria/cobros", <SecretaryPayments />],
  ["/secretaria/chat", <Chat />],
];

const PHARMACY_ROUTES = [
  ["/farmacia", <PharmacyInbox />],
  ["/farmacia/ventas", <PharmacySales />],
  ["/farmacia/historial", <PharmacySalesHistory />],
];

// Shared by doctor + secretary (clinical history viewer).
const DOCTOR_OR_SECRETARY_ROUTES = [
  ["/doctor/pacientes/:id", <DoctorPatientDetail />, { roles: ["doctor", "secretary"] }],
  ["/doctor/pacientes/nuevo-qr", <NewPatientQR />, { roles: ["doctor", "secretary"] }],
];

function renderRoutes(routes, defaultRoles) {
  return routes.map(([path, element, opts = {}]) => {
    const roles = opts.roles || defaultRoles;
    const inner = opts.gate ? (
      <DoctorOnboardingGate>{element}</DoctorOnboardingGate>
    ) : (
      element
    );
    return (
      <Route
        key={path}
        path={path}
        element={<Protected roles={roles}>{inner}</Protected>}
      />
    );
  });
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <SettingsProvider>
          <RealtimeProvider>
            <PatientCacheProvider>
            <BrowserRouter>
            <Toaster
              position="top-right"
              toastOptions={{
                unstyled: true,
                classNames: {
                  toast: "cai-toast w-[360px] rounded-[22px] px-4 py-3.5 flex items-center gap-3",
                  title: "text-[13px] font-semibold leading-snug",
                  description: "text-[12px] text-white/85 mt-0.5",
                  icon: "w-8 h-8 shrink-0 rounded-full bg-white/20 border border-white/30 grid place-items-center [&>svg]:w-4 [&>svg]:h-4",
                  actionButton: "ml-auto shrink-0 h-8 px-3.5 rounded-full bg-white text-[#5B8FD0] text-[12px] font-semibold hover:bg-white/90",
                  cancelButton: "shrink-0 h-8 px-3 rounded-full bg-white/20 text-white text-[12px]",
                  closeButton: "bg-white/20 text-white border-white/30",
                  error: "cai-toast-error",
                  warning: "cai-toast-warning",
                },
              }}
            />
            <Routes>
              {/* Public */}
              <Route path="/login" element={<Login />} />
              <Route path="/crear-contrasena" element={<CreatePasswordPage />} />
              <Route path="/registro" element={<Register />} />
              <Route path="/privacidad" element={<LegalPage />} />
              <Route path="/terminos" element={<LegalPage />} />
              <Route path="/legal" element={<LegalPage />} />
              <Route path="/verificar-email" element={<VerifyEmail />} />
              <Route path="/recuperar-contrasena" element={<ForgotPassword />} />
              <Route path="/restablecer-contrasena" element={<ResetPassword />} />
              <Route path="/cita/confirmar" element={<AppointmentConfirm />} />
              <Route path="/scan/:token" element={<ScanCapture />} />
              <Route path="/r/:token" element={<PublicReferral />} />

              {/* Semi-public: logged in but not yet routed to a role dashboard */}
              <Route
                path="/verifica-tu-correo"
                element={
                  <Protected roles={["doctor"]}>
                    <VerifyYourEmail />
                  </Protected>
                }
              />
              <Route
                path="/elegir-especialidad"
                element={
                  <Protected roles={["doctor"]}>
                    <SelectSpecialty />
                  </Protected>
                }
              />
              <Route
                path="/onboarding"
                element={
                  <Protected roles={["doctor"]}>
                    <RequireVerifiedEmail>
                      <Onboarding />
                    </RequireVerifiedEmail>
                  </Protected>
                }
              />

              {/* Aris — Full-screen assistant for doctors with feature flag */}
              <Route
                path="/aris"
                element={
                  <Protected roles={["doctor"]}>
                    <Aris />
                  </Protected>
                }
              />

              <Route path="/payment/success" element={<Protected roles={["doctor"]}><PaymentResult /></Protected>} />
              <Route path="/payment/cancel" element={<Protected roles={["doctor"]}><PaymentResult cancelled /></Protected>} />

              {/* Printable prescription — any logged-in role */}
              <Route
                path="/recetas/:id/imprimir"
                element={
                  <Protected roles={["doctor", "secretary", "pharmacy", "admin"]}>
                    <PrescriptionPrint />
                  </Protected>
                }
              />

              {/* Printable referral — doctor + admin */}
              <Route
                path="/referidos/:id/imprimir"
                element={
                  <Protected roles={["doctor", "admin"]}>
                    <ReferralPrint />
                  </Protected>
                }
              />

              {/* Authenticated app shell (Layout with sidebar/topbar) */}
              <Route element={<Protected><Layout /></Protected>}>
                {/* Shared (any logged-in role) */}
                <Route path="/perfil" element={<Protected><ProfilePage /></Protected>} />
                <Route path="/info" element={<Protected><InfoPage /></Protected>} />

                {renderRoutes(ADMIN_ROUTES, ["admin"])}
                {renderRoutes(DOCTOR_ROUTES, ["doctor"])}
                {renderRoutes(DOCTOR_OR_SECRETARY_ROUTES, ["doctor"])}
                {renderRoutes(SECRETARY_ROUTES, ["secretary"])}
                {renderRoutes(PHARMACY_ROUTES, ["pharmacy"])}
              </Route>

              {/* Redirects */}
              <Route path="/" element={<HomeRedirect />} />
              <Route path="*" element={<HomeRedirect />} />
            </Routes>
          </BrowserRouter>
          </PatientCacheProvider>
          </RealtimeProvider>
        </SettingsProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}
